import {
  all,
  call,
  select,
  put,
  debounce,
  takeEvery
} from 'redux-saga/effects';
import {
  types as teamTypes,
  siteReportActions,
  siteReportAlertsActions,
  alertResolveActions,
  scoresAlertsActions,
  trainingSessionActions
} from 'ducks/team/actions';
import { getOrgDivTeam, getOrgDiv } from 'ducks/auth/selectors';
import { openSnackbar } from 'ducks/auth/actions';
import { fetchDataWorkerSaga, postDataWorkerSaga } from './util';
import {
  fetchTeamMembers,
  fetchTeamScoresIndividual,
  fetchTeamSiteReport,
  fetchSiteReportAlerts,
  fetchActivityScores,
  fetchActivityHistogram,
  fetchTeamAlerts,
  suppressAlert,
  fetchTeamAlertsConfig,
  saveTeamAlertsConfig,
  fetchTeamsAlertsScores,
  fetchSessionSearch,
  fetchActiveUsersLatest,
  fetchActiveUsersHistogram,
  fetchTrainingSessions,
  fetchTrainingSession,
  fetchTeamAlgorithmConfig,
  saveTeamAlgorithmConfig
} from 'services/api';
import { overallScore } from 'utils/overallScore';
import moment from 'moment';

function* getTeamLocators(action) {
  try {
    const { org, div, team } = yield select(getOrgDivTeam);
    yield* fetchDataWorkerSaga(teamTypes.locators, fetchTeamMembers, {
      org,
      div,
      team,
      ...action.payload
    });
  } catch (err) {
    console.log(err);
  }
}

function* getIndividualScores(action) {
  try {
    let { org, div, ...params } = yield select(getOrgDivTeam);
    yield* fetchDataWorkerSaga(
      teamTypes.scoresHistory,
      fetchTeamScoresIndividual,
      {
        org,
        div,
        ...params,
        ...action.payload
      },
      {},
      res => {
        res.user_scores.forEach(user_scores => {
          user_scores.values.forEach(values => {
            values.metrics.overallScore = overallScore(values.metrics, {
              has_underscores: false
            });
          });
        });
        return res;
      }
    );
  } catch (err) {
    console.log(err);
  }
}

function* getActivityHistogram(action) {
  try {
    const { org, div, team } = yield select(getOrgDivTeam);
    yield* fetchDataWorkerSaga(
      teamTypes.teamActivityHistograms,
      fetchActivityHistogram,
      { ...action.payload, orgId: org, teamId: team, divId: div },
      {}
    );
  } catch (err) {
    console.log(err);
  }
}

function* getSiteReports(action) {
  //console.log(JSON.stringify(action.payload));
  const count = parseInt(action.payload.pageSize);
  const offset = parseInt(action.payload.page) * count;
  try {
    let _filters = [];
    _filters =
      action.payload.search &&
      JSON.parse(action.payload.search)?.map(f => f.filter);

    const zoom = action.payload.zoom;
    const geo_bounds = action.payload.geo_bounds;
    const apiParams = {
      after: moment(action.payload.from).unix(),
      before: moment(action.payload.to).unix(),
      filters: _filters,
      count: count,
      offset: offset,
      sort_key: action.payload.orderBy,
      sort_direction: action.payload.orderDirection
    };

    if (zoom !== null && zoom !== undefined) {
      apiParams.zoom = zoom;
      apiParams.agg = zoom < 12 ? 1 : 0;
    } else {
      apiParams.agg = 0;
    }

    if (geo_bounds !== null && geo_bounds !== undefined) {
      apiParams.geo_bounds = geo_bounds;
    }

    yield* fetchDataWorkerSaga(
      teamTypes.siteReports,
      fetchSessionSearch,
      apiParams,
      {
        //...action.payload,
      },
      res => {
        const isClusterData =
          res.body?.length > 0 && res.body[0].count !== undefined;

        const body = res.body.map(item => {
          const baseItem = {
            ...item,
            lat: item.geo_point.lat,
            lng: item.geo_point.lon
          };

          if (isClusterData) {
            return {
              ...baseItem,
              id: item.key,
              count: item.count,
              isCluster: true,
              overall_score: item.stats?.score?.avg || 0
            };
          }

          return {
            ...baseItem,
            id: item.pgid,
            overall: overallScore(item),
            isCluster: false
          };
        });

        return { ...res, body };
      }
    );
  } catch (err) {
    console.log(err);
  }
}

function* getActiveUsers(action) {
  //console.log(JSON.stringify(action.payload));
  const count = parseInt(action.payload.pageSize);
  const offset = parseInt(action.payload.page) * count;
  try {
    let _filters = [];
    _filters =
      action.payload.search &&
      JSON.parse(action.payload.search)
        ?.filter(f => f.filter.includes('team|') || f.filter.includes('user|'))
        .map(f => f.filter);
    yield* fetchDataWorkerSaga(
      teamTypes.activeUsers,
      fetchActiveUsersLatest,
      {
        after: moment(action.payload.from).unix(),
        before: moment(action.payload.to).unix(),
        filters: _filters,
        count: count,
        offset: offset,
        sort_key: action.payload.orderBy,
        sort_direction: action.payload.orderDirection
      },
      {
        //...action.payload,
      },
      res => {
        const rows = Array.isArray(res.body) ? res.body : res.body?.data || [];
        return {
          ...res,
          body: rows.map(item => ({
            ...item,
            id: item.most_recent_session?.pgid,
            user_name: item.most_recent_session?.user_name || item.user_label,
            team_name: item.most_recent_session?.team_name,
            timestamp: item.most_recent_session?.timestamp,
            location: item.most_recent_session?.location_short,
            overall_score: item.most_recent_session?.overall_score
          }))
        };
      }
    );
  } catch (err) {
    console.log(err);
  }
}

function* getActiveUsersHistogram(action) {
  try {
    let _filters = [];
    _filters =
      action.payload.search &&
      JSON.parse(action.payload.search)
        ?.filter(f => f.filter.includes('team|') || f.filter.includes('user|'))
        .map(f => f.filter);
    yield* fetchDataWorkerSaga(
      teamTypes.activeUsersHistogram,
      fetchActiveUsersHistogram,
      {
        after: moment(action.payload.from).unix(),
        before: moment(action.payload.to).unix(),
        interval: 'day',
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
        filters: _filters
      },
      {}
    );
  } catch (err) {
    console.log(err);
  }
}

function* getSiteReport(action) {
  try {
    const { sessionId, ...payloadRest } = action?.payload;
    // if we don't have sessionId, clear state and return without fetching
    if (!sessionId) {
      yield put(siteReportActions.clear());
      return;
    }

    const { org, team, div } = yield select(getOrgDivTeam);
    yield* fetchDataWorkerSaga(
      teamTypes.siteReport,
      fetchTeamSiteReport,
      {
        org,
        team,
        sessionId,
        div
      },
      {
        ...payloadRest,
        projection:
          'utilityType,id,userId,teamId,locatorDetails,latlon,startTime,duration,geo,numPoints,numAssets,modalFrequency,minCurrent,location,metrics,distanceWalked,spanDistance,walkingSpeed,activity,overall'
      },
      res => ({
        ...res,
        overall: overallScore(res)
      })
    );
  } catch (err) {
    console.log(err);
  }
}

function* getSiteReportAlerts(action) {
  try {
    // if we don't have sessionId, clear state and return without fetching
    if (!action?.payload?.sessionId) {
      yield put(siteReportAlertsActions.clear());
      return;
    }

    const { org, div, team } = yield select(getOrgDivTeam);

    yield* fetchDataWorkerSaga(
      teamTypes.siteReportAlerts,
      fetchSiteReportAlerts,
      null,
      {
        div,
        owner: team && `${org}/teams/${team}`,
        ...action.payload
      }
    );
  } catch (err) {
    console.log(err);
  }
}

function* getTeamActivity(action) {
  try {
    const { org, div, team } = yield select(getOrgDivTeam);
    yield* fetchDataWorkerSaga(
      teamTypes.activityHistory,
      fetchActivityScores,
      { ...action.payload, org, div, team },
      null
    );
  } catch (err) {
    console.log(err);
  }
}

function* getTeamAlerts(action) {
  try {
    const { org, div, team } = yield select(getOrgDivTeam);
    yield* fetchDataWorkerSaga(teamTypes.alerts, fetchTeamAlerts, null, {
      div,
      owner: `${org}/teams/${team}`,
      ...action.payload
    });
  } catch (err) {
    console.log(err);
  }
}

function* resolveTeamAlert(action) {
  const { id, issueType, comment, resolution } = action.payload;
  try {
    const { org, team } = yield select(getOrgDivTeam);
    const { id: responseId, resolved } = yield call(
      suppressAlert,
      { id, owner: `${org}/teams/${team}` },
      { issueType, comment, resolution }
    );

    if (responseId) {
      yield put(alertResolveActions.success({ id: responseId, resolved }));
      if (resolved) {
        yield put(openSnackbar('success', 'Alert resolved successfully'));
      } else {
        yield put(openSnackbar('warning', 'Alert saved successfully'));
      }
    } else {
      yield put(alertResolveActions.failure({ id }));
      yield put(openSnackbar('error', 'Error resolving alert'));
    }
  } catch (err) {
    console.log(err);
    yield put(alertResolveActions.failure({ id }));
    yield put(openSnackbar('error', 'Error resolving alert'));
  }
}

function* getTeamAlertsSettings(action) {
  try {
    const { org, div, team } = yield select(getOrgDivTeam);
    yield* fetchDataWorkerSaga(
      teamTypes.alertsSettings,
      fetchTeamAlertsConfig,
      { org, div, team },
      null,
      res => res.reduce((d, x) => ((d[x.id] = x), d), {})
    );
  } catch (err) {
    console.log(err);
  }
}

function* saveAlgorithmSettings(action) {
  try {
    const { org, div, team } = yield select(getOrgDivTeam);
    yield* postDataWorkerSaga(
      teamTypes.saveAlgorithmSettings,
      saveTeamAlgorithmConfig,
      { org, div, team },
      action.payload
    );
  } catch (err) {
    console.log(err);
  }
}

function* getTeamAlgorithmSettings(action) {
  try {
    const { org, div, team } = yield select(getOrgDivTeam);
    yield* fetchDataWorkerSaga(
      teamTypes.algorithmSettings,
      fetchTeamAlgorithmConfig,
      { org, div, team },
      null,
      res => res.scores
    );
  } catch (err) {
    console.log(err);
  }
}

function* saveAlertsSettings(action) {
  try {
    const { org, div, team } = yield select(getOrgDivTeam);
    yield* postDataWorkerSaga(
      teamTypes.saveAlertsSettings,
      saveTeamAlertsConfig,
      { org, div, team },
      action.payload
    );
  } catch (err) {
    console.log(err);
  }
}

function* getAlertsScores(action) {
  try {
    const { org, div } = yield select(getOrgDiv);
    if (!div) {
      throw new Error('Division is not available!');
    }
    yield* fetchDataWorkerSaga(
      teamTypes.scoresAlerts,
      fetchTeamsAlertsScores,
      {
        org,
        div,
        ...action.payload
      },
      {}
    );
  } catch (err) {
    yield put(scoresAlertsActions.failure());
  }
}

function* getTrainingSessions(action) {
  try {
    yield* fetchDataWorkerSaga(
      teamTypes.trainingSessions,
      fetchTrainingSessions,
      action.payload
    );
  } catch (err) {
    console.log(err);
  }
}

function* getTrainingSession(action) {
  try {
    const { sessionId } = action?.payload || {};
    // if we don't have sessionId, clear state and return without fetching
    if (!sessionId) {
      yield put(trainingSessionActions.clear());
      return;
    }

    yield* fetchDataWorkerSaga(
      teamTypes.trainingSession,
      fetchTrainingSession,
      sessionId
    );
  } catch (err) {
    console.log(err);
  }
}

export default function* sagas() {
  yield all([
    yield debounce(50, teamTypes.locators.REQUEST, getTeamLocators),
    yield debounce(50, teamTypes.scoresHistory.REQUEST, getIndividualScores),
    yield debounce(50, teamTypes.siteReports.REQUEST, getSiteReports),
    yield debounce(50, teamTypes.activeUsers.REQUEST, getActiveUsers),
    yield debounce(
      50,
      teamTypes.activeUsersHistogram.REQUEST,
      getActiveUsersHistogram
    ),
    yield debounce(50, teamTypes.siteReport.REQUEST, getSiteReport),
    yield debounce(50, teamTypes.siteReportAlerts.REQUEST, getSiteReportAlerts),
    yield debounce(50, teamTypes.activityHistory.REQUEST, getTeamActivity),
    yield debounce(
      50,
      teamTypes.teamActivityHistograms.REQUEST,
      getActivityHistogram
    ),
    yield debounce(50, teamTypes.alerts.REQUEST, getTeamAlerts),
    yield takeEvery(teamTypes.alertResolve.REQUEST, resolveTeamAlert),
    yield debounce(50, teamTypes.alertsSettings.REQUEST, getTeamAlertsSettings),
    yield debounce(
      50,
      teamTypes.algorithmSettings.REQUEST,
      getTeamAlgorithmSettings
    ),
    yield takeEvery(
      teamTypes.saveAlgorithmSettings.REQUEST,
      saveAlgorithmSettings
    ),
    yield takeEvery(teamTypes.saveAlertsSettings.REQUEST, saveAlertsSettings),
    yield debounce(50, teamTypes.scoresAlerts.REQUEST, getAlertsScores),
    yield debounce(50, teamTypes.trainingSessions.REQUEST, getTrainingSessions),
    yield debounce(50, teamTypes.trainingSession.REQUEST, getTrainingSession)
  ]);
}
